// Documentos SSO (sso_documents y bucket sso-documents), §17 ítem 70.

import { rutaEnBucket, type BucketArchivos } from './ruta-bucket';

/**
 * Las categorías de sso_documents_category_check: las 8 de 20261014000000:305,
 * 'politica' e 'informe' (M10, 20261020000000; bloque 5b, E) y 'registro'
 * (M13, 20261023000000; bloque 5d, A).
 */
export const SSO_DOC_CATEGORIES = [
  'reglamento', 'politica', 'informe', 'registro', 'acta', 'permiso', 'certificado',
  'procedimiento', 'plan_emergencia', 'matriz_riesgos', 'otro',
] as const;

export type SSODocCategory = typeof SSO_DOC_CATEGORIES[number];

/** Ítem sugerido al elegir una categoría (solo si el usuario aún no eligió uno). */
export const ITEM_SUGERIDO_POR_CATEGORIA: Partial<Record<SSODocCategory, string>> = { politica: 'GA-03' };

/** Ítems que se muestran primero en el selector según la categoría. */
export const ITEMS_PRIMERO_POR_CATEGORIA: Partial<Record<SSODocCategory, string[]>> = {
  politica: ['GA-03'],
  informe:  ['GA-07', 'GA-11'],
  registro: ['GA-04', 'GA-05', 'GA-06', 'GA-08', 'GA-09', 'GA-10'],
};

/**
 * Texto para buscar sin distinguir tildes ni mayúsculas (bloque 5d, A3):
 * "Certificación", "CERTIFICACION" y "certificacion" quedan iguales (la ñ, como n).
 */
export function normalizarBusqueda(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Opciones cuya etiqueta contiene lo escrito (vacío = todas). */
export function filtrarOpciones<T extends { label: string }>(opciones: T[], consulta: string): T[] {
  const q = normalizarBusqueda(consulta);
  if (!q) return opciones;
  return opciones.filter(o => normalizarBusqueda(o.label).includes(q));
}

export function ordenarOpcionesItem<T extends { id: string }>(opciones: T[], categoria: string): T[] {
  const primero = ITEMS_PRIMERO_POR_CATEGORIA[categoria as SSODocCategory] ?? [];
  if (primero.length === 0) return opciones;
  const arriba = primero.map(id => opciones.find(o => o.id === id)).filter((o): o is T => !!o);
  return [...arriba, ...opciones.filter(o => !primero.includes(o.id))];
}

// ── Tipos de archivo (bloque 5b, B) ─────────────────────────────────────────

/**
 * Extensión → tipo MIME aceptado por el bucket sso-documents (M10). Se sube con
 * este contentType y no con el que informa el navegador, que varía por sistema.
 * Las versiones con macros (.xlsm, .docm, .pptm) y los ejecutables no están.
 */
export const SSO_TIPOS_ARCHIVO: Record<string, string> = {
  pdf:  'application/pdf',
  doc:  'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls:  'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt:  'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  jpg:  'image/jpeg',
  jpeg: 'image/jpeg',
  png:  'image/png',
};

/** Para el atributo accept del input: ".pdf,.doc,…". */
export const SSO_ACCEPT = Object.keys(SSO_TIPOS_ARCHIVO).map(e => `.${e}`).join(',');

// ── AutoCAD solo en algunos ítems (bloque 5d, G) ────────────────────────────

/**
 * Ítems que aceptan planos de AutoCAD. Kevin lo pidió para la Matriz de Riesgos
 * (GT-04); puede cambiar a GT-03 (mapa de riesgos) cuando lo confirme.
 */
export const ITEMS_CON_AUTOCAD: string[] = ['GT-04'];

/**
 * .dwg y .dxf con sus tipos registrados en IANA. El navegador suele mandar el
 * .dwg como application/octet-stream (o vacío): ese dato no se usa; se valida
 * por la extensión y se sube con este contentType, así el bucket solo necesita
 * estos dos tipos (M13) y no application/octet-stream.
 */
export const SSO_TIPOS_AUTOCAD: Record<string, string> = {
  dwg: 'image/vnd.dwg',
  dxf: 'image/vnd.dxf',
};

export function itemAceptaAutocad(itemId: string | null | undefined): boolean {
  return !!itemId && ITEMS_CON_AUTOCAD.includes(itemId);
}

/** accept del input según el ítem elegido: .dwg y .dxf solo en ITEMS_CON_AUTOCAD. */
export function acceptSSO(itemId: string | null | undefined): string {
  if (!itemAceptaAutocad(itemId)) return SSO_ACCEPT;
  return [SSO_ACCEPT, ...Object.keys(SSO_TIPOS_AUTOCAD).map(e => `.${e}`)].join(',');
}

/** Límite del bucket sso-documents (se mantiene en M10). */
export const SSO_MAX_MB = 20;

export const MENSAJE_TIPO_NO_PERMITIDO = 'Este tipo de archivo no está permitido. Sube PDF, Word, Excel, PowerPoint, JPG o PNG.';
export const MENSAJE_AUTOCAD_SOLO_ITEM =
  `Los planos de AutoCAD (.dwg, .dxf) solo se aceptan en ${ITEMS_CON_AUTOCAD.join(', ')}. Para este ítem sube PDF, Word, Excel, PowerPoint, JPG o PNG.`;
export const MENSAJE_TAMANO = `El archivo supera ${SSO_MAX_MB} MB.`;
export const MENSAJE_ERROR_SUBIDA = 'No se pudo subir el archivo. Intenta de nuevo.';

// Solo claves propias: "x.constructor" o "x.tostring" no son tipos de archivo.
function propio(tabla: Record<string, string>, clave: string): boolean {
  return Object.prototype.hasOwnProperty.call(tabla, clave);
}

function extension(nombre: string): string | null {
  const punto = nombre.lastIndexOf('.');
  if (punto <= 0) return null;
  return nombre.slice(punto + 1).toLowerCase();
}

/** contentType para subir; .dwg y .dxf solo si el ítem los acepta. */
export function tipoArchivoSSO(nombre: string, itemId?: string | null): string | null {
  const ext = extension(nombre);
  if (!ext) return null;
  if (propio(SSO_TIPOS_ARCHIVO, ext)) return SSO_TIPOS_ARCHIVO[ext];
  if (propio(SSO_TIPOS_AUTOCAD, ext) && itemAceptaAutocad(itemId)) return SSO_TIPOS_AUTOCAD[ext];
  return null;
}

/**
 * El archivo que se pasa a upload(). Con un File, storage-js arma un multipart y
 * NO usa la opción contentType: el bucket ve el tipo que puso el navegador
 * (un .dwg llega como application/octet-stream o vacío; a veces también un
 * .xlsx). Por eso se envuelve en un File con el tipo según la extensión; no
 * copia los datos. Si el tipo ya coincide, se usa el mismo archivo.
 */
export function archivoParaSubir(archivo: File, itemId?: string | null): File {
  const tipo = tipoArchivoSSO(archivo.name, itemId);
  if (!tipo || archivo.type === tipo) return archivo;
  return new File([archivo], archivo.name, { type: tipo, lastModified: archivo.lastModified });
}

export function esArchivoAutocad(nombre: string): boolean {
  const ext = extension(nombre);
  return !!ext && propio(SSO_TIPOS_AUTOCAD, ext);
}

/** Mensaje para el usuario o null si el archivo se puede subir para ese ítem. */
export function validarArchivoSSO(archivo: { name: string; size: number }, itemId?: string | null): string | null {
  if (!tipoArchivoSSO(archivo.name, itemId)) {
    return esArchivoAutocad(archivo.name) ? MENSAJE_AUTOCAD_SOLO_ITEM : MENSAJE_TIPO_NO_PERMITIDO;
  }
  if (archivo.size > SSO_MAX_MB * 1024 * 1024) return MENSAJE_TAMANO;
  return null;
}

/**
 * Traduce el error de Storage a uno de los tres mensajes de la pantalla. El
 * detalle técnico lo deja quien llama en console.error.
 */
export function mensajeErrorSubida(error: { message?: string; statusCode?: string | number } | null | undefined): string {
  const texto = `${error?.message ?? ''} ${error?.statusCode ?? ''}`.toLowerCase();
  if (texto.includes('mime') || texto.includes('415')) return MENSAJE_TIPO_NO_PERMITIDO;
  if (texto.includes('size') || texto.includes('413') || texto.includes('too large')) return MENSAJE_TAMANO;
  return MENSAJE_ERROR_SUBIDA;
}

// ── Varios archivos por documento (bloque 5b, H; tabla sso_document_files, M11) ──

// 'lista_asistencia' y 'registro_fotografico': M13 (bloque 5d, B); el Anexo 1
// pide registro fotográfico en los informes de GA-07 y GA-11.
export const SSO_KINDS_ARCHIVO = ['principal', 'resolucion', 'qr', 'lista_asistencia', 'registro_fotografico', 'anexo'] as const;
export type SSOKindArchivo = typeof SSO_KINDS_ARCHIVO[number];

export const SSO_KIND_LABELS: Record<SSOKindArchivo, string> = {
  principal:            'Principal',
  resolucion:           'Resolución',
  qr:                   'QR',
  lista_asistencia:     'Lista de asistencia',
  registro_fotografico: 'Registro fotográfico',
  anexo:                'Anexo',
};

/** Tipos que se eligen para un archivo adicional (el principal va aparte). */
export const SSO_KINDS_ADICIONALES = SSO_KINDS_ARCHIVO.filter(
  (k): k is Exclude<SSOKindArchivo, 'principal'> => k !== 'principal',
);

/**
 * file_size_kb que se guarda (bloque 7, D; §17 ítem 118): 0 si el archivo pesa
 * menos de 1024 bytes (se muestra "< 1 KB"); si no, los KB redondeados. Antes,
 * Math.round(bytes / 1024) guardaba 1 desde 512 bytes y se veía "1 KB".
 */
export function kbParaGuardar(bytes: number): number {
  return bytes < 1024 ? 0 : Math.round(bytes / 1024);
}

/** Tamaño para la pantalla (bloque 6, E3): sin dato "—"; menos de 1 KB "< 1 KB". */
export function tamanoLegible(kb: number | null | undefined): string {
  if (kb == null) return '—';
  if (kb < 1) return '< 1 KB';
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// ── Eliminar con todas las versiones (bloque 6, E2; §17 ítem 105) ─────────────

export interface VersionDocumento {
  id: string;
  version: number;
  file_url: string;
  is_current: boolean;
}

export interface PasoBorrado {
  id: string;
  version: number;
  is_current: boolean;
  rutas: string[];   // rutas del bucket que se borran con esta versión
}

/**
 * Qué se borra al eliminar un documento: todas sus versiones (mismo título y
 * categoría, como el historial) con sus archivos. Las archivadas van primero y
 * la vigente al final: si algo falla, el documento sigue visible para
 * reintentar. Una ruta compartida entre versiones se borra una sola vez.
 */
export function planBorradoDocumento(versiones: VersionDocumento[], archivos: { document_id: string; file_path: string }[]): PasoBorrado[] {
  const yaAsignadas = new Set<string>();
  const ordenadas = [...versiones].sort((a, b) =>
    Number(a.is_current) - Number(b.is_current) || a.version - b.version);
  return ordenadas.map(v => {
    const propias = rutasDelDocumento(v.file_url, archivos.filter(a => a.document_id === v.id));
    const rutas = propias.filter(r => !yaAsignadas.has(r));
    rutas.forEach(r => yaAsignadas.add(r));
    return { id: v.id, version: v.version, is_current: v.is_current, rutas };
  });
}

export function resumenBorrado(plan: PasoBorrado[]): { versiones: number; archivos: number } {
  return { versiones: plan.length, archivos: plan.reduce((n, p) => n + p.rutas.length, 0) };
}

/** Rutas del paso que remove() no confirmó (vacío = se borraron todas). */
export function rutasNoBorradas(paso: PasoBorrado, borradas: { name: string }[] | null | undefined): string[] {
  const ok = new Set((borradas ?? []).map(b => b.name));
  return paso.rutas.filter(r => !ok.has(r));
}

/** Rutas a borrar del bucket al eliminar un documento: la del file_url y las de sus archivos, sin repetir. */
export function rutasDelDocumento(fileUrl: string, archivos: { file_path: string }[]): string[] {
  const principal = rutaStorageDesdeUrl(fileUrl);
  return Array.from(new Set([...(principal ? [principal] : []), ...archivos.map(a => a.file_path)]));
}

/** ¿El error de PostgREST indica que la tabla todavía no existe (M11 sin aplicar)? */
export function esTablaInexistente(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  return error.code === '42P01' || error.code === 'PGRST205'
    || /does not exist|could not find the table/i.test(error.message ?? '');
}

/**
 * Ruta del objeto en Storage a partir del valor guardado en file_url: la URL
 * pública (hasta M30) o la ruta (bloque 10).
 *
 * getPublicUrl() aplica encodeURI (un espacio queda como %20), así que la ruta
 * se decodifica antes de pasarla a remove(); sin esto, un archivo con espacios
 * en el nombre no se borraba y la fila sí (lo que dejó huérfano el PDF del 15/10).
 */
export function rutaStorageDesdeUrl(url: string, bucket: BucketArchivos = 'sso-documents'): string | null {
  return rutaEnBucket(bucket, url);
}

/** Fechas del documento (bloque 5, B1): todas manuales salvo la de carga. */
export function validarFechasDocumento(aprobadoDesde: string, vigenteHasta: string): string | null {
  if (aprobadoDesde && vigenteHasta && vigenteHasta < aprobadoDesde) {
    return '"Vigente hasta" no puede ser anterior a "Aprobado desde".';
  }
  return null;
}

/**
 * Aviso que no bloquea (bloque 5b, F3). Con "Vigente hasta" anterior a
 * "Aprobado desde" igual bloquea validarFechasDocumento (y el CHECK de M9).
 */
export function avisoVigencia(aprobadoDesde: string, vigenteHasta: string): string | null {
  if (aprobadoDesde && vigenteHasta && vigenteHasta <= aprobadoDesde) {
    return 'La vigencia termina el mismo día o antes de la aprobación; el documento saldrá caducado.';
  }
  return null;
}

/** "Vencido" se calcula solo con "Vigente hasta"; sin esa fecha no vence. */
export function documentoVencido(vigenteHasta: string | null, hoy: string): boolean {
  return !!vigenteHasta && vigenteHasta < hoy;
}

/** Hoy en formato YYYY-MM-DD, en la hora local. */
export function hoyISO(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface CamposEditables {
  title:             string;
  category:          string;
  checklist_item_id: string | null;
  approved_from:     string | null;
  expiry_date:       string | null;
}

/** Lo que cambió al editar (para audit_logs): { campo: { antes, despues } }. */
export function cambiosDocumento(antes: CamposEditables, despues: CamposEditables): Record<string, { antes: string | null; despues: string | null }> {
  const cambios: Record<string, { antes: string | null; despues: string | null }> = {};
  (Object.keys(antes) as (keyof CamposEditables)[]).forEach(k => {
    const a = antes[k] || null;
    const d = despues[k] || null;
    if (a !== d) cambios[k] = { antes: a, despues: d };
  });
  return cambios;
}

/**
 * GA-02 (bloque 5, B3): se cumple con el RHS aprobado subido a mano en
 * Documentos SSO (versión vigente y no vencida). Las filas que dejó el
 * generador del RHS (ruta /<empresa>/risso/) no cuentan.
 */
export function ga02CumplePorDocumentos(
  docs: { file_url: string; is_current: boolean; expiry_date: string | null }[],
  hoy: string,
): boolean {
  return docs.some(d => d.is_current && !d.file_url.includes('/risso/') && !documentoVencido(d.expiry_date, hoy));
}
